import {
  ExternalLinkType,
  LegacyProductCode,
  LocationsApi,
  Permission,
} from '@up2stay/up2promo-api-client';
import type {
  LocationRoutingWarning,
  ProductFormLocation,
} from '@up2stay/up2promo-commons/products';
import { Button, Input, Label } from '@up2stay/up2promo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  isValidFacebookUrl,
  isValidInstagramHandle,
  isValidMenuUrl,
  isValidTiktokHandle,
} from '@/core/components/locations/location-detail-external-services-tab';
import {
  externalLinksFromLocation,
  toExternalLinkItemDtos,
} from '@/core/components/locations/location-link-entry.types';
import { usePermissions } from '@/core/hooks/use-permissions';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

function linkTypeForWarning(
  warning: LocationRoutingWarning,
): ExternalLinkType | null {
  switch (warning) {
    case 'missingInstagram':
      return ExternalLinkType.INSTAGRAM;
    case 'missingTiktok':
      return ExternalLinkType.TIKTOK;
    case 'missingFacebook':
      return ExternalLinkType.FACEBOOK;
    case 'missingMenu':
      return ExternalLinkType.MENU;
    default:
      return null;
  }
}

function validateValue(type: ExternalLinkType, value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'required';
  if (type === ExternalLinkType.INSTAGRAM && !isValidInstagramHandle(trimmed)) {
    return 'instagram';
  }
  if (type === ExternalLinkType.TIKTOK && !isValidTiktokHandle(trimmed)) {
    return 'tiktok';
  }
  if (type === ExternalLinkType.FACEBOOK && !isValidFacebookUrl(trimmed)) {
    return 'facebook';
  }
  if (type === ExternalLinkType.MENU && !isValidMenuUrl(trimmed)) {
    return 'menu';
  }
  return null;
}

/**
 * When the picked location lacks IG/TT/FB/menu for this product type, let
 * managers add it here instead of bouncing to Locations settings.
 */
export function ProductsSetupLocationSocialFix({
  productType,
  location,
  warning,
  onSaved,
}: {
  productType: LegacyProductCode | undefined;
  location: ProductFormLocation | undefined;
  warning: LocationRoutingWarning | null;
  onSaved: () => void;
}) {
  const dict = useDictionary();
  const setup = (dict as { products?: { setup?: Record<string, string> } })
    .products?.setup;
  const locationsExternal = (
    dict as {
      locations?: {
        detail?: { externalServices?: Record<string, string | undefined> };
      };
    }
  ).locations?.detail?.externalServices;
  const workspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const linkType = warning ? linkTypeForWarning(warning) : null;
  const canEdit =
    can(Permission.LOCATIONS_MANAGE) &&
    !!location &&
    !!workspace?.id &&
    !!linkType;

  if (!canEdit || !linkType || !location || !workspace?.id) {
    return null;
  }

  const isHandle =
    linkType === ExternalLinkType.INSTAGRAM ||
    linkType === ExternalLinkType.TIKTOK;

  const title =
    linkType === ExternalLinkType.INSTAGRAM
      ? (setup?.locationSocialFixTitleIg ?? '')
      : linkType === ExternalLinkType.TIKTOK
        ? (setup?.locationSocialFixTitleTt ?? '')
        : linkType === ExternalLinkType.FACEBOOK
          ? (setup?.locationSocialFixTitleFb ?? '')
          : (setup?.locationSocialFixTitleMe ?? '');

  const description =
    setup?.locationSocialFixDescription ??
    (productType === LegacyProductCode.IG
      ? setup?.locationSocialFixDescriptionIg
      : undefined);

  const placeholder =
    linkType === ExternalLinkType.INSTAGRAM
      ? (setup?.usernamePlaceholderIg ?? '@username')
      : linkType === ExternalLinkType.TIKTOK
        ? (setup?.usernamePlaceholderTt ?? '@username')
        : (setup?.customUrlPlaceholder ?? 'https://');

  const handleSave = async () => {
    const invalid = validateValue(linkType, value);
    if (invalid) {
      const msg =
        invalid === 'instagram'
          ? (locationsExternal?.instagramInvalid ??
            setup?.locationSocialFixInvalid)
          : invalid === 'tiktok'
            ? (locationsExternal?.tiktokInvalid ??
              setup?.locationSocialFixInvalid)
            : invalid === 'facebook'
              ? (locationsExternal?.facebookInvalid ??
                setup?.locationSocialFixInvalid)
              : invalid === 'menu'
                ? (locationsExternal?.menuUrlInvalid ??
                  setup?.locationSocialFixInvalid)
                : (setup?.locationSocialFixRequired ?? '');
      setError(msg ?? null);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const existing = externalLinksFromLocation(location.externalLinks);
      const next = [
        ...existing.filter((e) => e.type !== linkType),
        { type: linkType, value: value.trim() },
      ];
      await LocationsApi.update({
        path: { workspaceId: workspace.id, id: location.id },
        body: { externalLinks: toExternalLinkItemDtos(next) },
      });
      await queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.root,
      });
      toast.success(setup?.locationSocialFixSuccess);
      setValue('');
      onSaved();
    } catch {
      toast.error(setup?.locationSocialFixError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-nested)] border border-border/80 bg-muted/25 px-4 py-3.5 sm:px-5 sm:py-4">
      <div className="flex flex-col gap-1">
        <p className="text-[15px] font-semibold text-foreground">{title}</p>
        {description ? (
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="setup-location-social">
          {isHandle
            ? (setup?.usernameLabel ?? '')
            : (setup?.customUrlLabel ?? '')}
        </Label>
        <Input
          id="setup-location-social"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={!isCapacitorNative()}
          enterKeyHint="done"
          aria-invalid={!!error}
          className={error ? 'border-destructive' : undefined}
        />
        {error ? (
          <p className="text-[13.5px] text-destructive">{error}</p>
        ) : null}
      </div>
      <Button
        type="button"
        variant="primary"
        size="lg"
        className="w-full sm:w-auto sm:self-start"
        disabled={saving || !value.trim()}
        isLoading={saving}
        onClick={() => void handleSave()}
      >
        {setup?.locationSocialFixSave ?? setup?.create}
      </Button>
    </div>
  );
}
